import React, { useState, useEffect, useMemo } from 'react'; // Agregado useMemo si es necesario
import ReactApexChart from 'react-apexcharts';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import BurbujasLoader from './UIElements/BurbujasLoader';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
// Se elimina getMatrizFechas
import { getInstancias } from '../services/instancias.service';

dayjs.locale('es');

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`monthly-chart-tabpanel-${index}`}
      aria-labelledby={`monthly-chart-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const DetalleMesChart = ({ sidebarOpen }) => {
  const [allInstancias, setAllInstancias] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [dataCupo, setDataCupo] = useState([]);
  const [dataCursosInician, setDataCursosInician] = useState([]); // Renombrado para claridad
  const [labels, setLabels] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  const [baseOptions, setBaseOptions] = useState({
    chart: {
      type: 'bar',
      height: 400,
      toolbar: { show: true },
      redrawOnParentResize: true,
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        // borderRadius: 4 // Opcional para barras redondeadas
      },
    },
    dataLabels: {
      enabled: false, // Habilitar si se quieren etiquetas en las barras
      // formatter: function (val) {
      //   return val;
      // },
      // offsetY: -20,
      // style: {
      //   fontSize: '12px',
      //   colors: ["#304758"]
      // }
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories: [],
      title: {
        text: 'Mes',
      },
      labels: {
        style: {
          fontSize: '10px' // Ajustar tamaño si los nombres de los meses son largos
        }
      }
    },
    yaxis: {
      title: {
        text: 'Cantidad',
      },
      min: 0,
      // tickAmount: 5, // Para controlar el número de ticks
      // labels: {
      //   formatter: function (val) {
      //     return val.toFixed(0); // Asegurar enteros en el eje Y
      //   }
      // }
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      // theme: 'dark', // Opcional
      x: {
        formatter: function (value, { seriesIndex, dataPointIndex, w }) {
          // 'value' aquí es el índice de la categoría, usamos 'labels' para obtener el nombre del mes
          return labels[dataPointIndex] || '';
        }
      },
      y: {
        formatter: function (val) {
          return val; // El valor numérico de la barra
        },
      },
    },
    title: {
      text: 'Resumen Mensual',
      align: 'center',
      style: {
        fontSize: '16px'
      }
    },
    legend: { show: true, position: 'top', horizontalAlign: 'center', offsetY: 10 }, // Mostrar leyenda
  });

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const todasInstancias = await getInstancias();
        if (!Array.isArray(todasInstancias)) {
          throw new Error("La respuesta de getInstancias no es un array");
        }
        setAllInstancias(todasInstancias);

        // Extraer años únicos disponibles
        const yearsSet = new Set();
        todasInstancias.forEach(inst => {
          if (inst.fecha_inicio_curso) {
            const y = dayjs(inst.fecha_inicio_curso).year();
            if (y) yearsSet.add(y);
          }
        });
        const sortedYears = Array.from(yearsSet).sort((a, b) => b - a);
        setAvailableYears(sortedYears);

      } catch (error) {
        console.error('Error fetching instances:', error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (allInstancias.length === 0) return;

    // Filtrar instancias activas (no CANC) de la plataforma CC y del año seleccionado
    let instanciasFiltradas = allInstancias.filter(inst =>
      inst.estado_instancia !== 'CANC' &&
      inst.plataforma_dictado === 'CC'
    );

    if (selectedYear !== 'all') {
      instanciasFiltradas = instanciasFiltradas.filter(inst =>
        inst.fecha_inicio_curso && dayjs(inst.fecha_inicio_curso).year() === Number(selectedYear)
      );
    }

    // Agrupar por mes
    const datosPorMes = {};

    instanciasFiltradas.forEach(instancia => {
      if (instancia.fecha_inicio_curso) {
        const fechaInicio = dayjs(instancia.fecha_inicio_curso);
        const mesClave = fechaInicio.format('YYYY-MM');

        if (!datosPorMes[mesClave]) {
          datosPorMes[mesClave] = {
            cantidadCupoMensual: 0,
            cantidadCursosMensual: 0,
          };
        }
        datosPorMes[mesClave].cantidadCursosMensual += 1;
        datosPorMes[mesClave].cantidadCupoMensual += Number(instancia.cupo) || 0;
      }
    });

    const mesesClaves = Object.keys(datosPorMes).sort();

    if (mesesClaves.length === 0) {
      setDataCupo([]);
      setDataCursosInician([]);
      setLabels([]);
      setBaseOptions(prev => ({ ...prev, xaxis: { ...prev.xaxis, categories: [] } }));
      return;
    }

    const currentLabels = mesesClaves.map(m => dayjs(m + '-01').format('MMMM YYYY'));
    const currentDatosCupo = mesesClaves.map(m => datosPorMes[m].cantidadCupoMensual);
    const currentDatosCursosInician = mesesClaves.map(m => datosPorMes[m].cantidadCursosMensual);

    setDataCupo(currentDatosCupo);
    setDataCursosInician(currentDatosCursosInician);
    setLabels(currentLabels);

    setBaseOptions(prevOptions => ({
      ...prevOptions,
      xaxis: {
        ...prevOptions.xaxis,
        categories: currentLabels,
      },
      title: {
        ...prevOptions.title,
        text: `Resumen Mensual ${selectedYear === 'all' ? '(Histórico)' : selectedYear}`,
      }
    }));
  }, [allInstancias, selectedYear]);

  const handleChangeTab = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Opciones específicas para cada gráfico, heredando de baseOptions
  const cupoChartOptions = useMemo(() => ({
    ...baseOptions,
    yaxis: {
      ...baseOptions.yaxis,
      title: {
        text: 'Total Cupo Mensual',
      },
    },
    colors: ['#36A2EB'], // Color para el gráfico de cupo
  }), [baseOptions]);

  const cursosChartOptions = useMemo(() => ({
    ...baseOptions,
    yaxis: {
      ...baseOptions.yaxis,
      title: {
        text: 'Total Cursos Iniciados Mensual',
      },
    },
    colors: ['#FF6384'], // Color para el gráfico de cursos
    // legend: { show: true, position: 'top' }
  }), [baseOptions]);


  const chartHeight = baseOptions?.chart?.height || 400;

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <FormControl fullWidth sx={{ mb: 3 }} disabled={loading}>
        <InputLabel id="select-anio-mensual-label">Año de Visualización</InputLabel>
        <Select
          labelId="select-anio-mensual-label"
          value={selectedYear}
          label="Año de Visualización"
          onChange={(e) => setSelectedYear(e.target.value)}
        >
          <MenuItem value="all"><em>Todos los años (Histórico)</em></MenuItem>
          {availableYears.map(year => (
            <MenuItem key={year} value={year}>{year}</MenuItem>
          ))}
        </Select>
      </FormControl>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: chartHeight + 50 }}>
          <BurbujasLoader />
        </Box>
      ) : labels.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: chartHeight + 50, border: '1px dashed grey', borderRadius: '4px', p: 2, textAlign: 'center' }}>
          <Typography>No hay datos mensuales disponibles para mostrar.</Typography>
        </Box>
      ) : (
        <Paper elevation={1} sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={handleChangeTab} aria-label="Gráficos de resumen mensual" centered>
              <Tab label="Cupo Mensual (Cursos Iniciados)" id="monthly-chart-tab-0" aria-controls="monthly-chart-tabpanel-0" />
              <Tab label="Cursos Iniciados Mensual" id="monthly-chart-tab-1" aria-controls="monthly-chart-tabpanel-1" />
            </Tabs>
          </Box>

          <TabPanel value={activeTab} index={0}>
            {/* Usar .some(v => v > 0) para mostrar solo si hay datos */}
            {dataCupo.length > 0 && dataCupo.some(v => v > 0) ? (
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <ReactApexChart
                  options={cupoChartOptions} // Usar opciones específicas
                  series={[{ name: 'Total Cupo Mensual (Cursos Iniciados)', data: dataCupo }]}
                  type="bar"
                  height={chartHeight}
                  width="100%"
                />
              </Box>
            ) : <Typography align="center" sx={{ p: 2 }}>No hay datos de cupo mensual.</Typography>}
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            {dataCursosInician.length > 0 && dataCursosInician.some(v => v > 0) ? (
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <ReactApexChart
                  options={cursosChartOptions} // Usar opciones específicas
                  series={[{ name: 'Total Cursos Iniciados Mensual', data: dataCursosInician }]}
                  type="bar"
                  height={chartHeight}
                  width="100%"
                />
              </Box>
            ) : <Typography align="center" sx={{ p: 2 }}>No hay datos de cursos iniciados mensuales.</Typography>}
          </TabPanel>
        </Paper>
      )}
    </Box>
  );
};

export default DetalleMesChart;